import { useRef, useState } from 'react';
import { Camera, MapPin, Smile, X } from 'lucide-react';
import {
  GIST_POST_MAX,
  GIST_POST_MIN,
  GIST_THREAD_MAX,
  GIST_VOICE_MAX_SECONDS,
  STICKER_IDS,
  type GistChipOption,
  type GistPhotoView,
  type GistPostView,
  type StickerId,
} from '@london-life/contracts';
import { describeError } from '@london-life/errors';
import { api } from '../lib/api';
import { notify } from '../lib/alerts';
import { actionKey } from '../lib/format';
import { StickerImage } from '../chat/message-list';
import { GistVoiceDraft, GistVoiceMic, type GistVoice } from './voice';
import { GistCamera } from './camera';
import { ApiPicture } from '../lib/api-picture';
import { canSnapshot } from '../lib/camera';

// one post being written: its words and its voice note
type Part = { text: string; voice?: GistVoice };

// how much of a post is left, as a ring (as Lagos): amber, then orange near the end, then red past it
function Ring({ length }: { length: number }) {
  const left = GIST_POST_MAX - length;
  const r = 9;
  const round = 2 * Math.PI * r;
  const done = Math.min(1, length / GIST_POST_MAX);
  const tone = left < 0 ? 'over' : left <= 20 ? 'near' : '';
  return (
    <span className={`gist-ring ${tone}`} aria-label={`${left} characters left`} role="img">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r={r} className="track" />
        <circle cx="12" cy="12" r={r} className="fill" strokeDasharray={round} strokeDashoffset={round * (1 - done)} />
      </svg>
      {left <= 20 && <small>{left}</small>}
    </span>
  );
}

// The composer (as Lagos's New post): what's happening, posts added under it as a thread (up to ten), each with
// words, a voice note (the mic records into the post you are on) or both; a tag from your life or a sticker;
// "Post", or "Post all" for a thread. Adding to your own thread: one post, "Add".
export function GistCompose({
  chips,
  postsLeftToday,
  append,
  onDone,
  onCancel,
}: {
  chips: GistChipOption[];
  postsLeftToday: number;
  // your thread's first post, to add to
  append?: GistPostView;
  onDone: (post: GistPostView | null) => void;
  onCancel: () => void;
}) {
  const [parts, setParts] = useState<Part[]>([{ text: '' }]);
  const [chip, setChip] = useState<string | null>(null);
  const [tags, setTags] = useState(false);
  const [stickers, setStickers] = useState(false);
  // the in-game camera, and the photo it took for the first post
  const [camera, setCamera] = useState(false);
  const [photo, setPhoto] = useState<GistPhotoView | null>(null);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const key = useRef<string | null>(null);
  const picked = chips.find((item) => `${item.kind}:${item.id}` === chip) ?? null;
  const words = parts.map((part) => ({ text: part.text.trim(), voice: part.voice }));
  // each post: words (three characters at least) or a voice note, or both
  const done = (part: { text: string; voice?: GistVoice }, index = 1) =>
    (part.text.length >= GIST_POST_MIN ||
      ((Boolean(part.voice) || (index === 0 && photo !== null)) && part.text.length === 0)) &&
    part.text.length <= GIST_POST_MAX;
  const ready = words.every((part, index) => done(part, index)) && (append !== undefined || postsLeftToday > 0);
  const setPart = (index: number, change: Partial<Part>) =>
    setParts((now) => now.map((item, at) => (at === index ? { ...item, ...change } : item)));
  const asPart = (part: (typeof words)[number]) => ({
    ...(part.text ? { text: part.text } : {}),
    ...(part.voice ? { voice: part.voice.id } : {}),
  });
  const room = append ? 1 : GIST_THREAD_MAX;
  const send = async (sticker?: StickerId) => {
    if (busy || (!sticker && !ready)) return;
    setBusy(true);
    key.current ??= actionKey('gist');
    try {
      if (append) {
        const reply = await api.gist.act(append.id, { action: 'append', ...asPart(words[0]!) });
        notify(reply.message ?? 'Added to your thread');
        onDone(null);
      } else {
        const reply = await api.gist.post(
          sticker
            ? { sticker, ...(picked ? { chip: { kind: picked.kind, id: picked.id } } : {}) }
            : {
                ...asPart(words[0]!),
                ...(photo ? { photo: photo.id } : {}),
                ...(words.length > 1 ? { more: words.slice(1).map(asPart) } : {}),
                ...(picked ? { chip: { kind: picked.kind, id: picked.id } } : {}),
              },
          key.current,
        );
        notify(words.length > 1 ? 'Your thread was posted' : 'Your post was sent');
        onDone(reply.post);
      }
      key.current = null;
    } catch (error) {
      key.current = null;
      notify(describeError(error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="gist-compose" role="form" aria-label={append ? 'Add to thread' : 'New post'}>
      <div className="gist-compose-top">
        <button type="button" className="gist-cancel" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="gist-post-button" disabled={busy || !ready} onClick={() => void send()}>
          {busy ? 'Posting…' : append ? 'Add' : parts.length > 1 ? 'Post all' : 'Post'}
        </button>
      </div>
      <ol className="gist-compose-parts">
        {parts.map((part, index) => (
          <li key={index} className={busy ? 'sending' : ''}>
            <span className="gist-line" aria-hidden="true" hidden={index === parts.length - 1} />
            <textarea
              value={part.text}
              rows={3}
              maxLength={GIST_POST_MAX}
              autoFocus={index === parts.length - 1}
              aria-label={index === 0 ? 'Your post' : `Post ${index + 1}`}
              placeholder={
                index === 0 ? (append ? 'Add another post' : "What's happening in London?") : `Post ${index + 1}`
              }
              onFocus={() => setActive(index)}
              onChange={(event) => setPart(index, { text: event.target.value })}
            />
            {part.voice && <GistVoiceDraft voice={part.voice} onRemove={() => setPart(index, { voice: undefined })} />}
            {index === 0 && photo && (
              <span className="gist-photo-draft">
                <ApiPicture path={photo.src} alt="Your photo" />
                <button type="button" aria-label="Remove the photo" onClick={() => setPhoto(null)}>
                  <X aria-hidden="true" />
                </button>
              </span>
            )}
            {index > 0 && (
              <button
                type="button"
                className="gist-remove-part"
                aria-label={`Remove post ${index + 1}`}
                onClick={() => {
                  setParts((now) => now.filter((_, at) => at !== index));
                  setActive(0);
                }}
              >
                <X aria-hidden="true" />
              </button>
            )}
          </li>
        ))}
      </ol>
      {parts.length < room && (
        <button
          type="button"
          className="gist-link gist-add-part"
          disabled={!done(words.at(-1)!, words.length - 1)}
          onClick={() => {
            setParts((now) => [...now, { text: '' }]);
            setActive(parts.length);
          }}
        >
          <span aria-hidden="true">+</span> Add another post
        </button>
      )}
      {picked && (
        <span className="gist-chip">
          {picked.emoji} {picked.label}
        </span>
      )}
      {tags && chips.length > 0 && (
        <div className="gist-chips" role="radiogroup" aria-label="Tag your post">
          {chips.map((item) => {
            const id = `${item.kind}:${item.id}`;
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={chip === id}
                className={chip === id ? 'active' : ''}
                onClick={() => setChip(chip === id ? null : id)}
              >
                {item.emoji} {item.label}
              </button>
            );
          })}
        </div>
      )}
      {stickers && (
        <div className="gist-stickers" role="group" aria-label="Post a sticker">
          {STICKER_IDS.map((id) => (
            <button key={id} type="button" disabled={busy} onClick={() => void send(id)}>
              <StickerImage id={id} size={56} />
            </button>
          ))}
        </div>
      )}
      <p className="gist-compose-note">
        Words, voice notes (up to {GIST_VOICE_MAX_SECONDS / 60} minutes) and photos of the game. Be kind: everything is
        checked; a photo shows once a moderator has looked at it.
      </p>
      <div className="gist-compose-tools">
        <GistVoiceMic
          disabled={busy || Boolean(parts[active]?.voice)}
          onVoice={(voice) => setPart(Math.min(active, parts.length - 1), { voice })}
        />
        {!append && chips.length > 0 && (
          <button
            type="button"
            aria-label="Add where you are, your home, a car or your job"
            aria-expanded={tags}
            onClick={() => setTags(!tags)}
          >
            <MapPin aria-hidden="true" />
          </button>
        )}
        {!append && (
          <button
            type="button"
            aria-label="Take a photo of the game"
            disabled={busy || photo !== null || !canSnapshot()}
            onClick={() => {
              setStickers(false);
              setCamera(true);
            }}
          >
            <Camera aria-hidden="true" />
          </button>
        )}
        {!append && parts.length === 1 && !parts[0]!.voice && !photo && (
          <button type="button" aria-label="Stickers" aria-expanded={stickers} onClick={() => setStickers(!stickers)}>
            <Smile aria-hidden="true" />
          </button>
        )}
        <span className="gist-compose-left">
          {!append && postsLeftToday <= 5 ? `${postsLeftToday} posts left today` : ''}
        </span>
        <Ring length={(parts[active]?.text ?? '').length} />
      </div>
      {camera && (
        <GistCamera
          onClose={() => setCamera(false)}
          onPhoto={(taken) => {
            setPhoto(taken);
            setCamera(false);
          }}
        />
      )}
    </div>
  );
}
